import React, { useState, useEffect } from 'react';
import { useUser } from 'context/user/User';
import styled, { keyframes } from 'styled-components';
import { getReservations, updateReservation, getReservationSettings } from 'api/requests/reservations';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';
import InfoTooltip from 'components/_UI/InfoTooltip';
import colors from 'theme/colors';
import { CalendarCheck, Check, X, CheckCheck, UserX, Settings } from 'lucide-react';
import ReservationStatusBadge from 'components/commons/ReservationStatusBadge';
import ConfirmDialog from 'modules/restaurant/menu/components/ConfirmDialog';
import ReservationSettingsModal from './ReservationSettingsModal';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const FILTERS = [
    { key: 'all',       label: 'Todas' },
    { key: 'pending',   label: 'Pendentes' },
    { key: 'confirmed', label: 'Confirmadas' },
    { key: 'completed', label: 'Concluídas' },
    { key: 'cancelled', label: 'Canceladas' },
    { key: 'no_show',   label: 'Não compareceu' },
];

export default function RestaurantReservationsPage() {
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;
    const { toasts, toast } = useToast();
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('pending');
    const [updatingId, setUpdatingId] = useState(null);
    const [cancelDialog, setCancelDialog] = useState({ open: false, id: null, name: '' });
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);
    const [settings, setSettings] = useState(null);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, [slug]);

    // Refetch on mount and on every tab change so new/updated reservations show up.
    // Filtering stays client-side; only the first load shows the full-page loading state.
    useEffect(() => {
        fetchReservations();
    }, [slug, activeFilter]);

    const fetchSettings = async () => {
        if (!slug) return;
        try {
            const data = await getReservationSettings(slug);
            setSettings(data);
        } catch (error) {
            console.error('Error fetching reservation settings', error);
        }
    };

    const fetchReservations = async () => {
        if (!slug) return;
        try {
            const data = await getReservations(slug);
            setReservations(data);
        } catch (error) {
            console.error('Error fetching reservations', error);
            toast.error('Erro ao carregar reservas.');
        } finally {
            setLoading(false);
        }
    };

    const applyStatusUpdate = async (reservationId, payload) => {
        try {
            setUpdatingId(reservationId);
            const updated = await updateReservation(slug, reservationId, payload);
            setReservations(prev => prev.map(r => r.id === reservationId ? updated : r));
            toast.success('Reserva atualizada com sucesso!');
        } catch (error) {
            console.error('Error updating reservation', error);
            toast.error('Erro ao atualizar a reserva.');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleConfirm = (reservation) => applyStatusUpdate(reservation.id, { status: 'confirmed' });
    const handleComplete = (reservation) => applyStatusUpdate(reservation.id, { status: 'completed' });
    const handleNoShow = (reservation) => applyStatusUpdate(reservation.id, { status: 'no_show' });

    const openCancelDialog = (reservation) => {
        setCancelReason('');
        setCancelDialog({ open: true, id: reservation.id, name: reservation.person_name });
    };

    const closeCancelDialog = () => {
        setCancelDialog({ open: false, id: null, name: '' });
        setCancelReason('');
    };

    const handleCancel = async () => {
        try {
            setCancelling(true);
            const updated = await updateReservation(slug, cancelDialog.id, { status: 'cancelled', cancel_reason: cancelReason || null });
            setReservations(prev => prev.map(r => r.id === cancelDialog.id ? updated : r));
            toast.success('Reserva cancelada.');
        } catch (error) {
            console.error('Error cancelling reservation', error);
            toast.error('Erro ao cancelar a reserva.');
        } finally {
            setCancelling(false);
            closeCancelDialog();
        }
    };

    const formatDate = (dateStr) => {
        try {
            const [year, month, day] = dateStr.split('-');
            return `${day}/${month}/${year}`;
        } catch (error) {
            return dateStr;
        }
    };

    const displayedReservations = activeFilter === 'all'
        ? reservations
        : reservations.filter(r => r.status === activeFilter);

    const pendingCount = reservations.filter(r => r.status === 'pending').length;

    if (!slug) {
        return (
            <PageWrapper>
                <NoEstablishmentState>
                    <NoEstablishmentIcon><CalendarCheck size={36} color='#999' /></NoEstablishmentIcon>
                    <NoEstablishmentTitle>Nenhum estabelecimento encontrado</NoEstablishmentTitle>
                    <NoEstablishmentHint>Crie um estabelecimento para gerenciar reservas.</NoEstablishmentHint>
                </NoEstablishmentState>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <ToastContainer toasts={toasts} />

            <PageHeader>
                <HeaderLeft>
                    <HeaderIcon><CalendarCheck size={20} color={panelAccent(colors.occasionalPurple)} /></HeaderIcon>
                    <div>
                        <PageTitle>Reservas</PageTitle>
                        <PageSubtitle>Gerencie os pedidos de reserva de mesa do seu estabelecimento</PageSubtitle>
                    </div>
                </HeaderLeft>
                <HeaderActions>
                    {settings && (
                        <CapacityIndicator>
                            {settings.max_party_size_per_slot != null
                                ? `Capacidade: ${settings.max_party_size_per_slot}/horário`
                                : 'Sem limite'}
                        </CapacityIndicator>
                    )}
                    <InfoTooltip type='none' bgColor={colors.night} text='Configurar reservas'>
                        <SettingsBtn onClick={() => setIsSettingsModalOpen(true)} aria-label='Configurar reservas'>
                            <Settings size={18} color={colors.nightDark} />
                        </SettingsBtn>
                    </InfoTooltip>
                </HeaderActions>
            </PageHeader>

            <FilterRow>
                {FILTERS.map(filter => (
                    <FilterPill
                        key={filter.key}
                        type='button'
                        $active={activeFilter === filter.key}
                        onClick={() => setActiveFilter(filter.key)}
                        aria-pressed={activeFilter === filter.key}
                    >
                        {filter.label}
                        {filter.key === 'pending' && pendingCount > 0 && <PillCount>{pendingCount}</PillCount>}
                    </FilterPill>
                ))}
            </FilterRow>

            <TableCard>
                <ContentWrapper>
                    {loading ? (
                        <LoadingContainer>
                            <Spinner />
                            <LoadingText>Carregando reservas...</LoadingText>
                        </LoadingContainer>
                    ) : displayedReservations.length === 0 ? (
                        <EmptyState>
                            <EmptyIconWrapper><CalendarCheck size={32} color={panelAccent(colors.occasionalPurple)} /></EmptyIconWrapper>
                            <EmptyText>Nenhuma reserva encontrada</EmptyText>
                            <EmptyHint>Reservas solicitadas pelos clientes na página do estabelecimento aparecerão aqui.</EmptyHint>
                        </EmptyState>
                    ) : (
                        <StyledTableContainer>
                            <StyledTable>
                                <thead>
                                    <tr>
                                        <th style={{ width: '20%' }}>Cliente</th>
                                        <th style={{ width: '15%' }}>Telefone</th>
                                        <th style={{ width: '12%' }}>Data</th>
                                        <th style={{ width: '10%' }}>Hora</th>
                                        <th style={{ width: '10%' }}>Pessoas</th>
                                        <th style={{ width: '13%' }}>Status</th>
                                        <th style={{ width: '20%', textAlign: 'right' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedReservations.map(reservation => (
                                        <tr key={reservation.id}>
                                            <td>
                                                <ItemName>{reservation.person_name}</ItemName>
                                                {reservation.note && <ItemSub>{reservation.note}</ItemSub>}
                                            </td>
                                            <td><ItemVal>{reservation.phone}</ItemVal></td>
                                            <td><ItemVal>{formatDate(reservation.reservation_date)}</ItemVal></td>
                                            <td><ItemVal>{reservation.reservation_time}</ItemVal></td>
                                            <td><ItemVal>{reservation.party_size}</ItemVal></td>
                                            <td>
                                                <ReservationStatusBadge status={reservation.status} />
                                                {reservation.status === 'cancelled' && reservation.cancel_reason && (
                                                    <ItemSub>{reservation.cancel_reason}</ItemSub>
                                                )}
                                            </td>
                                            <td>
                                                <ActionsWrapper>
                                                    {reservation.status === 'pending' && (
                                                        <>
                                                            <InfoTooltip type='none' bgColor={colors.night} text='Confirmar reserva'>
                                                                <ActionBtn onClick={() => handleConfirm(reservation)} disabled={updatingId === reservation.id} aria-label='Confirmar'>
                                                                    <Check size={16} color={colors.nightDark} />
                                                                </ActionBtn>
                                                            </InfoTooltip>
                                                            <InfoTooltip type='none' bgColor={colors.night} text='Cancelar reserva'>
                                                                <ActionBtn onClick={() => openCancelDialog(reservation)} disabled={updatingId === reservation.id} className='danger' aria-label='Cancelar'>
                                                                    <X size={16} color={colors.danger} />
                                                                </ActionBtn>
                                                            </InfoTooltip>
                                                        </>
                                                    )}
                                                    {reservation.status === 'confirmed' && (
                                                        <>
                                                            <InfoTooltip type='none' bgColor={colors.night} text='Marcar como concluída'>
                                                                <ActionBtn onClick={() => handleComplete(reservation)} disabled={updatingId === reservation.id} aria-label='Concluir'>
                                                                    <CheckCheck size={16} color={colors.nightDark} />
                                                                </ActionBtn>
                                                            </InfoTooltip>
                                                            <InfoTooltip type='none' bgColor={colors.night} text='Cliente não compareceu'>
                                                                <ActionBtn onClick={() => handleNoShow(reservation)} disabled={updatingId === reservation.id} aria-label='Não compareceu'>
                                                                    <UserX size={16} color={colors.nightDark} />
                                                                </ActionBtn>
                                                            </InfoTooltip>
                                                            <InfoTooltip type='none' bgColor={colors.night} text='Cancelar reserva'>
                                                                <ActionBtn onClick={() => openCancelDialog(reservation)} disabled={updatingId === reservation.id} className='danger' aria-label='Cancelar'>
                                                                    <X size={16} color={colors.danger} />
                                                                </ActionBtn>
                                                            </InfoTooltip>
                                                        </>
                                                    )}
                                                </ActionsWrapper>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </StyledTable>
                        </StyledTableContainer>
                    )}
                </ContentWrapper>
            </TableCard>

            <ConfirmDialog
                isOpen={cancelDialog.open}
                title='Cancelar reserva'
                message={`Tem certeza que deseja cancelar a reserva de "${cancelDialog.name}"?`}
                confirmLabel='Cancelar reserva'
                cancelLabel='Voltar'
                danger
                loading={cancelling}
                onConfirm={handleCancel}
                onCancel={closeCancelDialog}
            >
                <CancelReasonLabel htmlFor='cancel-reason'>Motivo (opcional)</CancelReasonLabel>
                <CancelReasonInput
                    id='cancel-reason'
                    value={cancelReason}
                    onChange={e => setCancelReason(e.target.value)}
                    placeholder='Ex: Mesa indisponível no horário solicitado'
                    rows={3}
                />
            </ConfirmDialog>

            <ReservationSettingsModal
                isOpen={isSettingsModalOpen}
                onClose={() => setIsSettingsModalOpen(false)}
                establishmentSlug={slug}
                settings={settings}
                onSuccess={setSettings}
                toast={toast}
            />
        </PageWrapper>
    );
}

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${colors.neutralBackground};
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 16px 14px;
    border-radius: 14px 14px 0 0;
    background: ${colors.neutralLight};
    flex-shrink: 0;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const HeaderIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const PageTitle = styled.h1`
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
    line-height: 1.3;
`;

const PageSubtitle = styled.p`
    font-size: 13px;
    color: #666;
    margin: 2px 0 0;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const CapacityIndicator = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #666;
    white-space: nowrap;
`;

const SettingsBtn = styled.button`
    width: 38px;
    height: 38px;
    border-radius: 12px;
    border: 1.5px solid ${colors.neutralDark};
    background: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    &:hover { border-color: ${panelAccent(colors.occasionalPurple)}; }
`;

const FilterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 12px 16px;
    background: ${colors.neutralLight};
`;

const FilterPill = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 14px;
    height: 34px;
    border-radius: 20px;
    border: 1.5px solid ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : colors.neutralDark};
    background: ${({ $active }) => $active ? panelAccentAlpha(colors.occasionalPurple, '12') : '#fff'};
    color: ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : '#666'};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
    &:hover { border-color: ${panelAccent(colors.occasionalPurple)}; color: ${panelAccent(colors.occasionalPurple)}; }
`;

const PillCount = styled.span`
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 20px;
    background: ${colors.danger};
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const TableCard = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 0 0 14px 14px;
    box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.04), 0 2px 12px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    min-height: 0;
`;

const ContentWrapper = styled.div`
    padding: 20px 16px 24px;
    flex: 1;
    overflow-y: auto;
`;

const StyledTableContainer = styled.div`
    overflow-x: auto;
    border-radius: 12px;
    border: 1px solid ${colors.neutralDark};
    background: #fff;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 760px;
    table-layout: fixed;

    th, td {
        padding: 16px;
        text-align: left;
        border-bottom: 1px solid ${colors.neutralDark};
        overflow-wrap: break-word;
    }

    th {
        font-size: 13px;
        font-weight: 600;
        color: #666;
        text-transform: uppercase;
        background: #fafafa;
        letter-spacing: 0.5px;
    }

    tr:last-child td {
        border-bottom: none;
    }

    tbody tr {
        transition: background-color 0.15s;
        &:hover {
            background-color: #fcfcfc;
        }
    }
`;

const ItemName = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ItemSub = styled.div`
    font-size: 12px;
    color: #888;
    margin-top: 2px;
`;

const ItemVal = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
`;

const ActionBtn = styled.button`
    background: transparent;
    border: none;
    border-radius: 8px;
    padding: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;

    &:hover {
        background: ${colors.neutralDark};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &.danger:hover {
        background: ${colors.danger}15;
    }
`;

const CancelReasonLabel = styled.label`
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 6px;
`;

const CancelReasonInput = styled.textarea`
    width: 100%;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    padding: 10px 12px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    color: ${colors.nightDark};
    &:focus { outline: none; border-color: ${panelAccent(colors.occasionalPurple)}; }
`;

// States
const NoEstablishmentState = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    height: 100%; padding: 40px 20px; text-align: center;
`;

const NoEstablishmentIcon = styled.div`
    width: 64px; height: 64px; border-radius: 50%; background: ${colors.neutralDark};
    display: flex; align-items: center; justify-content: center; margin-bottom: 16px;
`;

const NoEstablishmentTitle = styled.h2`
    font-size: 17px; font-weight: 600; color: ${colors.nightDark}; margin: 0 0 6px;
`;

const NoEstablishmentHint = styled.p`
    font-size: 14px; color: #666; margin: 0; max-width: 280px;
`;

const EmptyState = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 60px 20px; text-align: center;
`;

const EmptyIconWrapper = styled.div`
    width: 56px; height: 56px; border-radius: 50%; background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex; align-items: center; justify-content: center; margin-bottom: 14px;
`;

const EmptyText = styled.p`font-weight: 600; font-size: 16px; color: ${colors.nightDark}; margin: 0 0 6px;`;
const EmptyHint = styled.p`font-size: 13px; color: #666; margin: 0 0 18px;`;

const LoadingContainer = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    height: 200px; gap: 12px;
`;

const LoadingText = styled.span`
    font-size: 13px; color: #666;
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const Spinner = styled.div`
    width: 32px; height: 32px; border: 3px solid ${colors.neutralDark};
    border-top-color: ${panelAccent(colors.occasionalPurple)}; border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;
